import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem,
  Paper, Radio, RadioGroup, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Edit, Key } from '@mui/icons-material';
import gitApi from '../git/api';
import util from '../util';
import mcpApi from './api';

export const CAPABILITY_GROUPS = [
  {
    label: 'Content',
    items: [
      { id: 'content.list', label: 'Browse, search, and list metadata' },
      { id: 'content.read', label: 'Read file content and download' },
      { id: 'content.write', label: 'Create, write, upload, rename, move' },
      { id: 'content.delete', label: 'Delete files and folders' },
      { id: 'content.share', label: 'Change sharing / access mode' },
    ],
  },
  {
    label: 'Git',
    items: [
      { id: 'git.list', label: 'Browse repositories, trees, log, commits' },
      { id: 'git.read', label: 'Read blobs, diffs, and LFS assets' },
      { id: 'git.write', label: 'Commit files and manage branches' },
      { id: 'git.admin', label: 'Create/delete repos, settings, collaborators' },
    ],
  },
];

export const PRESETS = {
  list: ['content.list', 'git.list'],
  read: ['content.list', 'content.read', 'git.list', 'git.read'],
  full: [
    'content.list', 'content.read', 'content.write', 'content.delete', 'content.share',
    'git.list', 'git.read', 'git.write', 'git.admin',
  ],
};

const PRESET_LABELS = { list: 'List only', read: 'Read only', full: 'Full RW' };
const REPO_MODE_LABELS = { read: 'Read', write: 'Write', admin: 'Admin' };

export function presetFor(capabilities) {
  const set = new Set(capabilities || []);
  for (const [name, caps] of Object.entries(PRESETS)) {
    if (caps.length === set.size && caps.every((c) => set.has(c))) {
      return name;
    }
  }
  return 'custom';
}

export function allowedRepoModes(capabilities) {
  const caps = new Set(capabilities || []);
  if (!caps.has('git.list')) return [];
  if (caps.has('git.admin')) return ['read', 'write', 'admin'];
  if (caps.has('git.write')) return ['read', 'write'];
  return ['read'];
}

export function clampRepoModes(repoModes, capabilities) {
  const allowed = allowedRepoModes(capabilities);
  const order = ['read', 'write', 'admin'];
  const result = {};
  for (const [repoId, mode] of Object.entries(repoModes || {})) {
    if (allowed.includes(mode)) {
      result[repoId] = mode;
      continue;
    }

    const rank = order.indexOf(mode);
    const fallback = allowed
      .filter((candidate) => order.indexOf(candidate) < rank)
      .sort((a, b) => order.indexOf(b) - order.indexOf(a))[0];
    if (fallback) result[repoId] = fallback;
  }
  return result;
}

export function configSnippet(endpoint, token) {
  return JSON.stringify({
    mcpServers: {
      ifmw: {
        type: 'http',
        url: endpoint,
        headers: { Authorization: `Bearer ${token || '<your-token>'}` },
      },
    },
  }, null, 2);
}

const emptyForm = () => ({
  name: '',
  capabilities: [...PRESETS.read],
  preset: 'read',
  expiryMode: '30',
  customDays: '',
  allRepos: true,
  repoModes: {},
});

const EXPIRY_OPTIONS = [
  { value: 'never', label: 'Never expires' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'custom', label: 'Custom days' },
];

const McpPanel = () => {
  const [keys, setKeys] = useState([]);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reposLoading, setReposLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [repoQuery, setRepoQuery] = useState('');
  const [copied, setCopied] = useState('');

  const endpoint = `${util.GetFunctionRoot()}/mcp`;

  const loadKeys = async () => {
    setLoading(true);
    const res = await mcpApi.listKeys();
    if (res.ok) {
      setKeys(res.data?.data || []);
      setError('');
    } else {
      setError(res.data?.message || 'Failed to load MCP keys');
    }
    setLoading(false);
  };

  const loadRepos = async () => {
    setReposLoading(true);
    const res = await gitApi.listRepos('all', '');
    if (res.ok) {
      setRepos(res.data?.data || []);
    }
    setReposLoading(false);
  };

  useEffect(() => {
    loadKeys();
    loadRepos();
  }, []);

  const canUseGit = form.capabilities.some((c) => c.startsWith('git.'));
  const allowedModes = allowedRepoModes(form.capabilities);
  const selectedRepoCount = Object.keys(form.repoModes).length;
  const preset = presetFor(form.capabilities);

  const filteredRepos = useMemo(() => {
    const q = repoQuery.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter((r) => `${r.owner}/${r.name}`.toLowerCase().includes(q)
      || (r.description || '').toLowerCase().includes(q));
  }, [repos, repoQuery]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setRepoQuery('');
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (key) => {
    const capabilities = [...(key.capabilities || [])];
    const modes = {};
    for (const grant of key.gitRepoGrants || []) {
      if (!grant.missing) modes[grant.repoId] = grant.mode;
    }

    setEditing(key);
    setForm({
      name: key.name,
      capabilities,
      preset: presetFor(capabilities),
      expiryMode: 'keep',
      customDays: '',
      allRepos: key.allGitRepos,
      repoModes: clampRepoModes(modes, capabilities),
    });
    setRepoQuery('');
    setFormError('');
    setDialogOpen(true);
  };

  const setCapabilities = (capabilities) => {
    setForm((prev) => ({
      ...prev,
      capabilities,
      preset: presetFor(capabilities),
      repoModes: clampRepoModes(prev.repoModes, capabilities),
    }));
  };

  const toggleCapability = (id) => {
    const next = form.capabilities.includes(id)
      ? form.capabilities.filter((c) => c !== id)
      : [...form.capabilities, id];
    setCapabilities(next);
  };

  const applyPreset = (name) => {
    setCapabilities([...PRESETS[name]]);
  };

  const toggleRepo = (repoId, checked) => {
    setForm((prev) => {
      const next = { ...prev.repoModes };
      if (checked) next[repoId] = allowedModes[0] || 'read';
      else delete next[repoId];
      return { ...prev, repoModes: next };
    });
  };

  const setRepoMode = (repoId, mode) => {
    setForm((prev) => ({ ...prev, repoModes: { ...prev.repoModes, [repoId]: mode } }));
  };

  const resolveExpiry = () => {
    if (form.expiryMode === 'keep') return undefined;
    if (form.expiryMode === 'never') return editing ? 0 : null;
    if (form.expiryMode === 'custom') {
      const days = parseInt(form.customDays, 10);
      return Number.isFinite(days) && days > 0 ? days : NaN;
    }
    return parseInt(form.expiryMode, 10);
  };

  const save = async () => {
    setFormError('');
    if (!form.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (form.capabilities.length === 0) {
      setFormError('Select at least one capability');
      return;
    }
    if (canUseGit && !form.allRepos && selectedRepoCount === 0) {
      setFormError('Select at least one repository, or allow all repositories');
      return;
    }

    const expiresInDays = resolveExpiry();
    if (Number.isNaN(expiresInDays)) {
      setFormError('Custom expiry must be a positive number of days');
      return;
    }

    const body = {
      name: form.name.trim(),
      capabilities: form.capabilities,
      allGitRepos: canUseGit ? form.allRepos : true,
      gitRepoGrants: canUseGit && !form.allRepos
        ? Object.entries(form.repoModes).map(([repoId, mode]) => ({
            repoId,
            mode: allowedModes.includes(mode) ? mode : allowedModes[0],
          }))
        : [],
    };

    if (expiresInDays !== undefined) {
      body.expiresInDays = expiresInDays;
    }

    setSaving(true);
    const res = editing
      ? await mcpApi.updateKey(editing.id, body)
      : await mcpApi.createKey(body);
    setSaving(false);

    if (!res.ok) {
      setFormError(res.data?.message || 'Failed to save MCP key');
      return;
    }

    setDialogOpen(false);
    if (!editing) {
      setCreated({ token: res.data.token, key: res.data.data });
    }
    loadKeys();
  };

  const revoke = async (key) => {
    if (!window.confirm(`Revoke MCP key "${key.name}"? Clients using it will stop working immediately.`)) {
      return;
    }
    const res = await mcpApi.revokeKey(key.id);
    if (!res.ok) {
      setError(res.data?.message || 'Failed to revoke MCP key');
      return;
    }
    loadKeys();
  };

  const scopeLabel = (key) => {
    if (!(key.capabilities || []).some((c) => c.startsWith('git.'))) return 'no git';
    if (key.allGitRepos) return 'all repos';
    const count = (key.gitRepoGrants || []).length;
    return `${count} repo${count === 1 ? '' : 's'}`;
  };

  return (
    <Paper sx={{ backgroundColor: '#1d1d1d', p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Key />
        <Typography variant="h6">MCP API Keys</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, opacity: 0.7, mb: 2 }}>
        Let AI agents use IFMW through the Model Context Protocol. Keys are separate from Git
        personal access tokens and can be restricted by capability and by repository.
      </Typography>

      <Box sx={{ backgroundColor: '#101010', p: 1.5, borderRadius: 1, mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Endpoint</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontFamily: 'monospace', fontSize: 12.5, backgroundColor: '#000', p: 1, borderRadius: 1, flex: 1, overflowX: 'auto' }}>
            {endpoint}
          </Box>
          <Tooltip title={copied === 'endpoint' ? 'Copied' : 'Copy endpoint'}>
            <IconButton size="small" onClick={() => copy(endpoint, 'endpoint')}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography sx={{ fontSize: 12, opacity: 0.6, mt: 0.75 }}>
          Send <Box component="code" sx={{ fontFamily: 'monospace' }}>Authorization: Bearer &lt;key&gt;</Box> with
          every request. Keys are shown only once and can be revoked here at any time.
        </Typography>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        keys.length === 0
          ? <Typography sx={{ fontSize: 13, opacity: 0.6, mb: 1 }}>No MCP keys yet.</Typography>
          : keys.map((key) => (
            <Box key={key.id} sx={{ borderTop: '1px solid #2b2b2b', py: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{key.name}</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 11.5, opacity: 0.55 }}>{key.prefix}...</Typography>
                <Chip size="small" label={PRESET_LABELS[presetFor(key.capabilities)] || 'Custom'} />
                <Chip size="small" variant="outlined" label={scopeLabel(key)} />
                {key.expires && <Chip size="small" label={`expires ${new Date(key.expires).toLocaleDateString()}`} />}
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(key)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Revoke"><IconButton size="small" color="error" onClick={() => revoke(key)}><Delete fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
                {(key.capabilities || []).map((cap) => (
                  <Chip key={cap} size="small" variant="outlined" label={cap} sx={{ fontSize: 10.5 }} />
                ))}
                {key.lastUsed && (
                  <Typography sx={{ fontSize: 11, opacity: 0.5, ml: 0.5 }}>
                    last used {new Date(key.lastUsed).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </Box>
          ))
      )}

      <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2 }} onClick={openCreate}>
        Create MCP key
      </Button>
      {error && <Typography color="error" sx={{ mt: 1, fontSize: 13 }}>{error}</Typography>}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit MCP key' : 'Create MCP key'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Key name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Expiry</InputLabel>
              <Select
                label="Expiry"
                value={form.expiryMode}
                onChange={(e) => setForm({ ...form, expiryMode: e.target.value })}
              >
                {editing && <MenuItem value="keep">Keep current</MenuItem>}
                {EXPIRY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            {form.expiryMode === 'custom' && (
              <TextField
                size="small"
                label="Days"
                type="number"
                value={form.customDays}
                onChange={(e) => setForm({ ...form, customDays: e.target.value })}
                sx={{ width: 110 }}
              />
            )}
          </Box>

          <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 2.5, mb: 1 }}>Preset</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.keys(PRESETS).map((name) => (
              <Button
                key={name}
                size="small"
                variant={preset === name ? 'contained' : 'outlined'}
                onClick={() => applyPreset(name)}
              >
                {PRESET_LABELS[name]}
              </Button>
            ))}
            <Chip size="small" label="Custom" color={preset === 'custom' ? 'primary' : 'default'} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            {CAPABILITY_GROUPS.map((group) => (
              <Box key={group.label} sx={{ backgroundColor: '#151515', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{group.label}</Typography>
                {group.items.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    sx={{ display: 'flex', alignItems: 'center', m: 0, py: 0.25 }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={form.capabilities.includes(item.id)}
                        onChange={() => toggleCapability(item.id)}
                      />
                    )}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 12.5 }}>{item.label}</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, opacity: 0.5 }}>{item.id}</Typography>
                      </Box>
                    )}
                  />
                ))}
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Git repository access</Typography>
          {!canUseGit ? (
            <Typography sx={{ fontSize: 12.5, opacity: 0.6 }}>
              Enable at least one Git capability to configure repository scope.
            </Typography>
          ) : (
            <>
              <RadioGroup
                row
                value={form.allRepos ? 'all' : 'selected'}
                onChange={(e) => setForm({ ...form, allRepos: e.target.value === 'all' })}
              >
                <FormControlLabel value="all" control={<Radio size="small" />} label="All repositories I can access" />
                <FormControlLabel value="selected" control={<Radio size="small" />} label="Only selected repositories" />
              </RadioGroup>

              {!form.allRepos && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Filter repositories"
                    value={repoQuery}
                    onChange={(e) => setRepoQuery(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  {reposLoading ? <CircularProgress size={20} /> : (
                    <Box sx={{ maxHeight: 280, overflowY: 'auto', backgroundColor: '#151515', borderRadius: 1, p: 1 }}>
                      {filteredRepos.length === 0 && (
                        <Typography sx={{ fontSize: 12.5, opacity: 0.6, p: 1 }}>No repositories found.</Typography>
                      )}
                      {filteredRepos.map((repo) => {
                        const checked = Object.prototype.hasOwnProperty.call(form.repoModes, repo.id);
                        return (
                          <Box key={repo.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={(e) => toggleRepo(repo.id, e.target.checked)}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: 12.5 }} noWrap>{repo.owner}/{repo.name}</Typography>
                              <Typography sx={{ fontSize: 10.5, opacity: 0.5 }} noWrap>
                                {repo.visibility}{repo.description ? ` - ${repo.description}` : ''}
                              </Typography>
                            </Box>
                            <Select
                              size="small"
                              value={checked ? form.repoModes[repo.id] : ''}
                              displayEmpty
                              disabled={!checked}
                              onChange={(e) => setRepoMode(repo.id, e.target.value)}
                              sx={{ width: 110 }}
                            >
                              {!checked && <MenuItem value="">-</MenuItem>}
                              {allowedModes.map((mode) => (
                                <MenuItem key={mode} value={mode}>{REPO_MODE_LABELS[mode]}</MenuItem>
                              ))}
                            </Select>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: 11.5, opacity: 0.55, mt: 0.75 }}>
                    Repositories created later are not added automatically. Read is the minimum;
                    Write allows commits/branches; Admin adds repository settings and collaborators.
                  </Typography>
                </Box>
              )}
            </>
          )}

          {formError && <Typography color="error" sx={{ mt: 1.5, fontSize: 13 }}>{formError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (editing ? 'Save changes' : 'Create key')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>MCP key created</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1 }}>
            Copy the key now; it will not be shown again.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField fullWidth value={created?.token || ''} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
            <IconButton onClick={() => copy(created?.token || '', 'token')}>
              <ContentCopy />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Client configuration</Typography>
          <Box sx={{ fontFamily: 'monospace', fontSize: 11.5, backgroundColor: '#000', p: 1.25, borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {configSnippet(endpoint, created?.token)}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
            <Button size="small" startIcon={<ContentCopy />} onClick={() => copy(configSnippet(endpoint, created?.token), 'config')}>
              {copied === 'config' ? 'Copied' : 'Copy config'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreated(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default McpPanel;
